import type { BoxProps, SelectChangeEvent } from '@mui/material'
import { FormControl, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import type { SortOptions } from '@tunasong/models'
import type { Entity, EntityType } from '@tunasong/schemas'
import type React from 'react'
import type { FC } from 'react'
import invariant from 'tiny-invariant'
import { VBox } from '../layout/vertical-box.js'
import EntityFilter from './entity-filter.js'

export interface SortFilterProps extends SortOptions, Omit<BoxProps, 'order'> {
  filter?: EntityType[]
  /** If specified, used to determine the filter types */
  entities?: Entity[]
  onSort(spec: SortOptions): void
  onFilter(filter: EntityType[]): void
}

export const SortFilter: FC<SortFilterProps> = props => {
  const { entities, filter, sortBy, order = 'asc', type, elementOrder, onSort, onFilter, ...boxProps } = props

  const filterTypes = entities ? [...new Set(entities.map(e => e.type))].sort() : undefined

  const handleSortModeChange = (_: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (value === 'manual') {
      onSort({ type: 'order', elementOrder })
    } else {
      onSort({ type: 'field', sortBy: sortBy ?? 'name', order, elementOrder })
    }
  }

  const handleSortByChange = (event: SelectChangeEvent<string | number>) => {
    const value = event.target.value
    onSort({ type: 'field', sortBy: value, order, elementOrder })
  }
  const handleOrderChange = (event: SelectChangeEvent<string | number>) => {
    const value = event.target.value
    invariant(value === 'asc' || value === 'desc', 'Expected order to be asc or desc: ' + value)
    onSort({ type: 'field', sortBy, order: value as 'asc' | 'desc', elementOrder })
  }

  const isManual = type === 'order'
  const sortMode = isManual ? 'manual' : 'auto'

  return (
    <VBox
      component="form"
      sx={{
        width: '100%',
        gap: 2,
        ...boxProps.sx,
      }}
      {...boxProps}
    >
      <EntityFilter value={filter} types={filterTypes} onChange={(_, filter) => onFilter(filter)} />

      <ToggleButtonGroup value={sortMode} exclusive onChange={handleSortModeChange} size="small" aria-label="sort mode">
        <ToggleButton value="auto" aria-label="auto sort">
          <Typography variant="caption">Auto</Typography>
        </ToggleButton>
        <ToggleButton value="manual" aria-label="manual sort">
          <Typography variant="caption">Manual</Typography>
        </ToggleButton>
      </ToggleButtonGroup>

      {!isManual && (
        <>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="sort-by-label">Sort By</InputLabel>
            <Select
              labelId="sort-by-label"
              id="sort-by-select"
              value={sortBy ?? 'name'}
              label="Sort By"
              onChange={handleSortByChange}
            >
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="type">Type</MenuItem>
              <MenuItem value="updatedAt">Updated</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="sort-order-label">Sort Order</InputLabel>
            <Select
              labelId="sort-order-label"
              id="sort-order"
              value={order}
              label="Sort Order"
              onChange={handleOrderChange}
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
          </FormControl>
        </>
      )}
    </VBox>
  )
}

export default SortFilter
